import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { get } from '../../utils/ajax';
import { remoteRoutes, localRoutes } from '../../data/constants';
import { isStudent } from '../../data/appRoles';
import { IState } from '../../data/types';
import StudentHome from './StudentHome';
import CourseCatalog from './CourseCatalog';

interface IOnboardingContext {
  /** False until the one-time enrollment check has finished. */
  checked: boolean;
  /** True for non-students, and for students with a real (non-pending)
   *  enrollment. False only for a brand-new student who hasn't enrolled
   *  in anything yet. */
  hasEnrollments: boolean;
}

const OnboardingContext = createContext<IOnboardingContext>({
  checked: true,
  hasEnrollments: true,
});

/** Lets NavMenu (and anything else) know whether to show the restricted
 *  2-item nav or the full student nav. */
export const useOnboarding = () => useContext(OnboardingContext);

/**
 * The trimmed public signup no longer collects a hub/course, so a brand-new
 * student has nothing to do in the full portal yet. Until their first
 * enrollment is approved, they see only Home + Browse Courses — regardless
 * of what URL they request — instead of whatever ContentSwitch would
 * otherwise render. Mirrors EmploymentUpdateGate's shape (check once, then
 * either pass through or take over).
 */
const NewStudentGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const user = useSelector((state: IState) => state.core.user);
  const student = isStudent(user);
  const [checked, setChecked] = useState(false);
  const [hasEnrollments, setHasEnrollments] = useState(true);

  useEffect(() => {
    if (!student) {
      setChecked(true);
      return;
    }
    get(
      remoteRoutes.myCourses,
      (data: any) => {
        const list: any[] = Array.isArray(data) ? data : [];
        // A still-pending first enrollment doesn't unlock the full portal —
        // there's nothing to do there until a hub manager approves it.
        setHasEnrollments(
          list.some((e) => e.status !== 'Pending' && e.status !== 'Dropped'),
        );
      },
      // Never lock someone out of the app over a failed request.
      () => setHasEnrollments(true),
      () => setChecked(true),
    );
  }, [student, user?.contactId]);

  if (!checked) return null;

  const value: IOnboardingContext = { checked, hasEnrollments };

  if (student && !hasEnrollments) {
    return (
      <OnboardingContext.Provider value={value}>
        <Switch>
          <Route path={localRoutes.catalog} component={CourseCatalog} />
          <Route component={StudentHome} />
        </Switch>
      </OnboardingContext.Provider>
    );
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default NewStudentGate;
